import { useMemo, useState } from 'react';

import {
  useLoggedInUserPermissions,
  useLoggedInUserRoles,
} from '~/components/Auth';
import { Modules } from '~/data/modules';
import { useSelectedEmployeeId } from '~/hooks/employee/useSelectedEmployeeId';
import { isRoleHR, isRoleManager } from '~/services/Permissions';
import { Optional } from '~/types';

export type UseModulePageFooterOutput = {
  employeeId: number;
  isManager: boolean;
  showSolaIntelShortcut: boolean;
  anchorEl: Optional<HTMLButtonElement>;
  handleSolaIntelOpen: (event: React.MouseEvent<HTMLButtonElement>) => void;
  handleSolaIntelClose: () => void;
  openSolaIntel: boolean;
};

export const useModulePageFooter = (): UseModulePageFooterOutput => {
  const { enabledModules } = useLoggedInUserPermissions();
  const userRoles = useLoggedInUserRoles();
  const { selectedEmployeeId } = useSelectedEmployeeId();
  const isManager = useMemo(() => isRoleManager(userRoles), [userRoles]);
  const showSolaIntelShortcut = useMemo(
    () =>
      enabledModules.includes(Modules.SOLAINTEL) &&
      (isRoleHR(userRoles) || isManager),
    [enabledModules, isManager, userRoles],
  );

  const [anchorEl, setAnchorEl] =
    useState<Optional<HTMLButtonElement>>(undefined);
  const openSolaIntel = Boolean(anchorEl);

  const handleSolaIntelOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleSolaIntelClose = () => {
    setAnchorEl(undefined);
  };

  return {
    employeeId: selectedEmployeeId,
    isManager,
    showSolaIntelShortcut,
    anchorEl,
    handleSolaIntelOpen,
    handleSolaIntelClose,
    openSolaIntel,
  };
};
